import { DatesSetArg, EventClickArg, EventContentArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Box, Button, LinearProgress, TextField, Tooltip, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import React, { useRef, useState } from 'react';
import { ClaimType, EhrClaim } from '../../interfaces/billing';
import { ClaimStatusIcon } from './ClaimStatusIcon';

interface CalendarProps {
  onEventClick: (clickInfo: EventClickArg) => void;
  onDateSetClick: (startDate: string, endDate: string) => void;
  claims: EhrClaim[];
  isLoading: boolean;
  pageProgress?: { page: number; totalPages: number } | null;
  onCancel?: () => void;
}

const CALENDAR_RIGHT_OPTIONS = ['dayGridMonth', 'listWeek'].join(',');
const CALENDAR_LEFT_OPTIONS = 'prev,next today';

const renderEventContent = (eventContent: EventContentArg) => {
  const { event } = eventContent;
  const className = event.extendedProps.source === ClaimType.RPM ? 'rpm-event' : 'other-event';

  return (
    <Tooltip title={`Claim ID: ${event.id} - ${event.extendedProps.status}`} placement='top' arrow>
      <Box display='flex' alignItems='center' maxWidth='100%' overflow='hidden'>
        <ClaimStatusIcon
          status={event.extendedProps.status}
          source={event.extendedProps.source}
        />
        <Typography variant='body2' className={className} noWrap>
          {event.title}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export const ClaimsCalendar: React.FC<CalendarProps> = ({
  claims,
  isLoading,
  pageProgress,
  onCancel,
  onDateSetClick,
  onEventClick,
}) => {
  const calendarRef = useRef<FullCalendar>(null);
  const [displayedMonth, setDisplayedMonth] = useState<Moment>(moment.utc());

  const events = claims.map((claim) => ({
    id: `${claim.id}`,
    title: claim.corePatient?.firstName
      ? `${claim.corePatient?.firstName} ${claim.corePatient?.lastName}`
      : claim.id
      ? `Claim ID: ${claim.id?.toString()}`
      : 'Unknown',
    start: moment.utc(claim.serviceDate).toISOString(),
    end: moment.utc(claim.serviceDate).toISOString(),
    extendedProps: claim,
  }));

  const handleDatesSet = (arg: DatesSetArg) => {
    // Keep the month picker in sync with whatever range FullCalendar shows.
    // The calendar runs in UTC, so currentStart is midnight UTC on the 1st;
    // parse it as UTC to avoid rolling back a month in negative-offset zones.
    setDisplayedMonth(moment.utc(arg.view.currentStart));
    onDateSetClick(arg.startStr, arg.endStr);
  };

  const handleMonthJump = (value: Moment | null) => {
    if (!value || !value.isValid()) {
      return;
    }
    // Jump to the 1st of the selected month as a plain date string so the UTC
    // calendar lands on the right month regardless of the local timezone.
    // gotoDate fires datesSet, which routes through the existing fetch flow.
    calendarRef.current?.getApi().gotoDate(value.format('YYYY-MM-01'));
  };

  const progressValue =
    pageProgress && pageProgress.totalPages > 0
      ? Math.round((pageProgress.page / pageProgress.totalPages) * 100)
      : 0;

  return (
    <Box mt={4} position='relative'>
      <Box display='flex' alignItems='center' gap={2} mb={1} flexWrap='wrap'>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            views={['year', 'month']}
            openTo='month'
            label='Jump to month'
            value={displayedMonth}
            onChange={handleMonthJump}
            renderInput={(params) => <TextField size='small' {...params} sx={{ width: 200 }} />}
          />
        </LocalizationProvider>
        {isLoading && (
          <Box display='flex' alignItems='center' gap={1} flexGrow={1} minWidth={240}>
            <Box flexGrow={1}>
              <LinearProgress
                variant={pageProgress ? 'determinate' : 'indeterminate'}
                value={progressValue}
              />
            </Box>
            {pageProgress && (
              <Typography variant='caption' whiteSpace='nowrap'>
                page {pageProgress.page} / {pageProgress.totalPages}
              </Typography>
            )}
            {onCancel && (
              <Button size='small' color='inherit' onClick={onCancel}>
                Cancel
              </Button>
            )}
          </Box>
        )}
      </Box>
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
        initialView='dayGridMonth'
        headerToolbar={{
          left: CALENDAR_LEFT_OPTIONS,
          center: 'title',
          right: CALENDAR_RIGHT_OPTIONS,
        }}
        selectable={true}
        selectMirror={true}
        dayMaxEvents={true}
        weekends={true}
        events={events}
        eventClick={onEventClick}
        eventContent={renderEventContent}
        datesSet={handleDatesSet}
        timeZone='UTC'
        height={'calc(100vh - 225px)'}
      />
    </Box>
  );
};
